import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import HeroSlideshow from '../components/HeroSlideshow';
import WorkflowScroller from '../components/WorkflowScroller';
import LogoMarquee from '../components/LogoMarquee';
import PersonaCards from '../components/PersonaCards';
import SplitTimeline from '../components/SplitTimeline';
import SecurityOrbit from '../components/SecurityOrbit';
import Reveal from '../components/Reveal';

// CountUp Component
const CountUp = ({ end, duration = 2000, suffix = '', prefix = '' }) => {
    const [count, setCount] = useState(0);
    const [hasAnimated, setHasAnimated] = useState(false);
    const ref = useRef(null);

    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting && !hasAnimated) {
                    setHasAnimated(true);
                    let startTime;
                    const animate = (currentTime) => {
                        if (!startTime) startTime = currentTime;
                        const progress = Math.min((currentTime - startTime) / duration, 1);
                        const easeOut = 1 - Math.pow(1 - progress, 3);
                        setCount(Math.floor(easeOut * end));
                        if (progress < 1) {
                            requestAnimationFrame(animate);
                        } else {
                            setCount(end);
                        }
                    };
                    requestAnimationFrame(animate);
                }
            },
            { threshold: 0.5 }
        );

        if (ref.current) observer.observe(ref.current);
        return () => observer.disconnect();
    }, [end, duration, hasAnimated]);

    return <span ref={ref}>{prefix}{count.toLocaleString()}{suffix}</span>;
};

// Accordion FAQ item
const FaqItem = ({ q, a }) => {
    const [open, setOpen] = useState(false);
    return (
        <div className={`faq-item ${open ? 'open' : ''}`}>
            <button className="faq-q" onClick={() => setOpen((o) => !o)} aria-expanded={open}>
                <span>{q}</span>
                <svg className="faq-chevron" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M6 9l6 6 6-6" />
                </svg>
            </button>
            <div className="faq-a-wrap">
                <p className="faq-a">{a}</p>
            </div>
        </div>
    );
};

const Home = () => {
    return (
        <div className="page-content">
            {/* ═══════════════════════════════════════════
                HERO (slideshow)
            ═══════════════════════════════════════════ */}
            <HeroSlideshow />

            {/* ═══════════════════════════════════════════
                BACKED BY (marquee)
            ═══════════════════════════════════════════ */}
            <LogoMarquee />

            {/* ═══════════════════════════════════════════
                METRICS STRIP
            ═══════════════════════════════════════════ */}
            <section className="metrics-strip">
                <div className="metrics-strip-inner">
                    <div className="metric-pill">
                        <span className="metric-pill-value"><CountUp end={30} duration={1500} suffix="+" /></span>
                        <span className="metric-pill-label">pre-built broker workflows</span>
                    </div>
                    <div className="metric-pill-divider"></div>
                    <div className="metric-pill">
                        <span className="metric-pill-value"><CountUp end={150} duration={1200} suffix="+" /></span>
                        <span className="metric-pill-label">data source integrations</span>
                    </div>
                    <div className="metric-pill-divider"></div>
                    <div className="metric-pill">
                        <span className="metric-pill-value">2 min</span>
                        <span className="metric-pill-label">average time to deliverable</span>
                    </div>
                </div>
            </section>

            {/* ═══════════════════════════════════════════
                THE PROBLEM (Before / After)
            ═══════════════════════════════════════════ */}
            <section className="section split-section" id="about">
                <SplitTimeline />
            </section>

            {/* ═══════════════════════════════════════════
                WORKFLOWS (What You Can Build)
            ═══════════════════════════════════════════ */}
            <section className="section" id="product">
                <WorkflowScroller />

            </section>

            {/* ═══════════════════════════════════════════
                SOCIAL PROOF
            ═══════════════════════════════════════════ */}
            <section className="section proof-section">
                <div className="section-center section-head">
                    <h2 className="section-heading">
                        Built at <span className="gradient-text">MIT</span>, validated by brokers
                    </h2>
                </div>

                <Reveal className="proof-grid">
                    <div className="proof-quote-card">
                        <div className="proof-quote-icon">"</div>
                        <p className="proof-quote-text">
                            We're saving the equivalent of one full employee per executive summary deliverable.
                        </p>
                        <div className="proof-quote-author">
                            <div className="proof-avatar">VP</div>
                            <div>
                                <strong>VP of Operations</strong>
                                <span>National benefits advisory firm</span>
                            </div>
                        </div>
                    </div>
                    <div className="proof-quote-card">
                        <div className="proof-quote-icon">"</div>
                        <p className="proof-quote-text">
                            Avenir pushed me to think differently. It surfaced data points I wouldn't have normally thought to include.
                        </p>
                        <div className="proof-quote-author">
                            <div className="proof-avatar">DA</div>
                            <div>
                                <strong>Data Analyst</strong>
                                <span>National benefits advisory firm</span>
                            </div>
                        </div>
                    </div>
                </Reveal>
            </section>

            {/* ═══════════════════════════════════════════
                PERSONAS
            ═══════════════════════════════════════════ */}
            <section className="personas-section" id="solutions">
                <PersonaCards />
            </section>

            {/* ═══════════════════════════════════════════
                SECURITY & COMPLIANCE
            ═══════════════════════════════════════════ */}
            <section className="section compliance-section">
                <div className="section-center section-head">
                    <h2 className="section-heading">
                        Enterprise-grade security <span className="gradient-text">built to protect sensitive data and PHI</span>
                    </h2>
                </div>

                <SecurityOrbit />
            </section>

            {/* ═══════════════════════════════════════════
                OBJECTION HANDLING (FAQ-style)
            ═══════════════════════════════════════════ */}
            <section className="section">
                <div className="section-center section-head">
                    <h2 className="section-heading">
                        Questions brokers <span className="gradient-text">ask most</span>
                    </h2>
                </div>

                <div className="faq-list">
                    <FaqItem
                        q="How long does setup take?"
                        a="Most brokers run their first analysis within a day. Connect your sources, choose a workflow, and start. No implementation project, no IT ticket."
                    />
                    <FaqItem
                        q="Does this work with my existing tools?"
                        a="Avenir exports to PowerPoint, Excel, and PDF, and works alongside your Agency Management System (AMS) and quoting tools."
                    />
                    <FaqItem
                        q="What data formats do you accept?"
                        a="CSV, Excel, PDF claims files, plus direct integrations with major TPAs and PBMs. If your carrier sends a spreadsheet, Avenir reads it."
                    />
                    <FaqItem
                        q="Is the AI going to replace my analysts?"
                        a="No. Avenir handles the repetitive data preparation so your analysts can focus on interpretation, strategy, and client-facing work."
                    />
                    <FaqItem
                        q="Can I white-label the reports?"
                        a="Yes. Every deliverable carries your firm's logo, colors, and formatting."
                    />
                    <FaqItem
                        q="What about smaller employer groups?"
                        a="Avenir works for self-insured groups from 200 to 20,000+ lives. The same engine runs at 500 claims or 500,000."
                    />
                </div>
            </section>

            {/* ═══════════════════════════════════════════
                CTA
            ═══════════════════════════════════════════ */}
            <section className="section">
                <div className="cta-wrapper">
                    <div className="cta-glow"></div>
                    <div className="cta-box">
                        <h2 className="cta-title">See what Avenir finds in your data</h2>
                        <div className="cta-buttons">
                            <Link to="/contact" className="btn btn-primary btn-large">Book a Live Demo</Link>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default Home;